import { useState } from "react";
import { Search, Loader2, IdCard, Car, ShieldCheck, FileBadge, AlertTriangle, Lock, RotateCcw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { formatRUT } from "@/lib/rut";

const PRECIO_RUT = 7990;

/** Consulta por RUT del titular → lista sus vehículos → compra del Informe 360 por RUT ($7.990).
 *  Requiere sesión iniciada (protege el saldo de créditos premium de Boostr). */
export const CheckByRut = () => {
  const { login, user } = useAuth();
  const [rut, setRut] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [buyingPlate, setBuyingPlate] = useState("");

  const buscar = async () => {
    if (!user) { login(); return; }
    if (!rut.trim()) { setError("Ingresa el RUT del titular."); return; }
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/api/report/rut/lookup", { rut: rut.trim() });
      if (!data?.ok) {
        setError(data?.error || "No pudimos consultar el RUT. Inténtalo nuevamente.");
        setResult(null);
      } else {
        setResult(data);
      }
    } catch (err) {
      if (err?.response?.status === 401) login();
      else setError("No pudimos consultar el RUT. Inténtalo nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  const comprar = async (v) => {
    if (!user) { login(); return; }
    setBuyingPlate(v.patente);
    try {
      const { data } = await api.post("/api/payments/checkout", {
        package_id: "informe_rut",
        origin_url: window.location.origin,
        patente: v.patente,
        anio: v.anio ? Number(v.anio) : null,
        rut: result?.owner?.rut || rut.trim(),
      });
      window.location.href = data.url;
    } catch {
      setError("No pudimos iniciar el pago. Inténtalo nuevamente.");
      setBuyingPlate("");
    }
  };

  const reset = () => { setResult(null); setError(""); setRut(""); };

  return (
    <div data-testid="check-by-rut">
      <div className="mt-7 flex flex-col sm:flex-row gap-2 max-w-xl mx-auto">
        <Input
          value={rut}
          onChange={(e) => setRut(formatRUT(e.target.value))}
          onKeyDown={(e) => e.key === "Enter" && buscar()}
          placeholder="12.345.678-9"
          className="h-12 bg-white text-[#13315C] border-0 text-center sm:text-left font-mono-tf text-lg"
          data-testid="check-rut-input"
        />
        <Button onClick={buscar} disabled={loading} className="h-12 px-6 bg-[#F5B301] hover:bg-[#dca400] text-[#13315C] font-bold rounded-md whitespace-nowrap" data-testid="check-rut-btn">
          {loading ? <Loader2 className="animate-spin" size={18} /> : <><Search size={18} className="mr-1.5" /> Buscar titular</>}
        </Button>
      </div>
      {error && <p className="mt-3 text-sm text-red-300 text-center" data-testid="check-rut-error">{error}</p>}

      <>
        {result?.ok && (
          <div
            className="tf-fade-up mt-8 max-w-3xl mx-auto bg-white rounded-2xl p-6 md:p-8 text-left shadow-xl"
            data-testid="check-rut-result"
          >
            {/* Titular */}
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center shrink-0">
                  <IdCard size={20} className="text-[#0e9d6e]" />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Titular</p>
                  <p className="font-display font-black text-xl text-[#13315C]" data-testid="check-rut-owner-name">{result.owner?.nombre || "—"}</p>
                  <p className="text-sm text-slate-500 font-mono-tf" data-testid="check-rut-owner-rut">{result.owner?.rut}</p>
                </div>
              </div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-1 text-xs font-bold text-emerald-700">
                <ShieldCheck size={13} /> RUT validado · SRCeI
              </span>
            </div>

            {/* Badge de valor: patrimonio del titular (antes de pagar) → sube conversión */}
            {result.vehicles_service_enabled && result.vehicles?.length > 0 && (
              <div className="mt-4 rounded-xl bg-[#13315C] text-white px-4 py-3 flex items-center gap-2.5" data-testid="check-rut-patrimonio">
                <Car size={18} className="text-[#F5B301] shrink-0" />
                <p className="text-sm leading-snug">
                  Este titular tiene <strong className="font-bold text-[#F5B301]" data-testid="check-rut-patrimonio-count">{result.vehicles.length} {result.vehicles.length === 1 ? "vehículo" : "vehículos"}</strong> inscrito{result.vehicles.length === 1 ? "" : "s"} a su nombre. Verifica el patrimonio del vendedor antes de cerrar el trato.
                </p>
              </div>
            )}

            {/* Vehículos */}
            <div className="mt-6">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                <Car size={13} /> Vehículos inscritos a su nombre {result.vehicles_service_enabled ? `(${result.vehicles?.length || 0})` : ""}
              </p>

              {result.vehicles_service_enabled ? (
                result.vehicles?.length ? (
                  <div className="mt-3 space-y-2" data-testid="check-rut-vehicles">
                    {result.vehicles.map((v) => (
                      <div key={v.patente} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 p-3.5" data-testid={`check-rut-vehicle-${v.patente}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <Car size={18} className="text-slate-400 shrink-0" />
                          <div className="min-w-0">
                            <p className="font-mono-tf font-bold text-[#13315C]">{v.patente}</p>
                            <p className="text-xs text-slate-500 truncate">
                              {[v.marca, v.modelo].filter(Boolean).join(" ")}{v.anio ? ` · ${v.anio}` : ""}{v.tipo ? ` · ${v.tipo}` : ""}
                            </p>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => comprar(v)}
                          disabled={!!buyingPlate}
                          className="rounded-md bg-[#13315C] hover:bg-[#1C4076] text-white font-semibold shrink-0"
                          data-testid={`check-rut-buy-${v.patente}`}
                        >
                          {buyingPlate === v.patente ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <FileBadge size={14} className="mr-1.5" />}
                          Comprar Informe 360 · ${PRECIO_RUT.toLocaleString("es-CL")}
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-slate-500">El titular no tiene vehículos inscritos a su nombre.</p>
                )
              ) : (
                <div className="mt-3 rounded-xl border border-blue-200 bg-blue-50 p-4 flex items-start gap-2" data-testid="check-rut-service-notice">
                  <AlertTriangle size={16} className="text-blue-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-blue-800">Servicio «Vehículos por RUT» no disponible en este momento</p>
                    <p className="text-[12px] text-blue-700 leading-snug mt-0.5">{result.notice || "Inténtalo nuevamente más tarde."}</p>
                  </div>
                </div>
              )}
            </div>

            <p className="mt-5 flex items-center gap-1.5 text-xs text-slate-400">
              <Lock size={12} /> El Informe 360 por RUT incluye la verificación del titular: identidad validada, vehículos y propiedades a su nombre.
            </p>
            <button onClick={reset} className="mt-4 inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-[#13315C] transition-colors" data-testid="check-rut-reset-btn">
              <RotateCcw size={14} /> Consultar otro RUT
            </button>
          </div>
        )}
      </>
    </div>
  );
};
